"use client";

// Client-side calls go through the same-origin Next.js proxy (`/api/[...path]/route.ts`)
// rather than the backend host directly — the proxy resolves `x-domain-host` from the
// request and is the only way a browser can reach the backend (which otherwise lives on
// the private Docker network). See frontend/src/app/api/[...path]/route.ts.

export type RegisterCustomerPayload = {
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  city: string;
  password: string;
  confirmPassword: string;
  languageCode: string;
};

export type ApiResult<T> = {
  ok: boolean;
  status: number;
  data: T | null;
};

async function postJson<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  let data: T | null = null;
  try {
    data = (await response.json()) as T;
  } catch {
    data = null;
  }

  return { ok: response.ok, status: response.status, data };
}

export type EmailDeliveryStatus = "Dispatched" | "NotConfigured";

export type AccountActionResponse = {
  message: string;
  /** Domain-wide email-system availability at the moment of the request — never
   * information about whether this specific email address already had an account. */
  emailDeliveryStatus: EmailDeliveryStatus;
};

export async function registerCustomer(payload: RegisterCustomerPayload): Promise<ApiResult<AccountActionResponse>> {
  return postJson<AccountActionResponse>("/api/public/account/register", payload);
}

export async function resendConfirmation(email: string, languageCode: string): Promise<ApiResult<AccountActionResponse>> {
  return postJson<AccountActionResponse>("/api/public/account/resend-confirmation", { email, languageCode });
}

export type ConfirmEmailOutcome = "Success" | "AlreadyConfirmed" | "InvalidOrExpired";

export async function confirmEmail(userId: string, token: string): Promise<ConfirmEmailOutcome | null> {
  const url = `/api/public/account/confirm-email?userId=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const data = (await response.json()) as { outcome: ConfirmEmailOutcome };
    return data.outcome;
  } catch {
    return null;
  }
}

// ---------- Forgot / reset password (Faz 4) ----------

/** Same enumeration-safety contract as resendConfirmation/registerCustomer above — the
 * response is identical whether or not the email belongs to an account. */
export async function forgotPassword(email: string, languageCode: string): Promise<ApiResult<AccountActionResponse>> {
  return postJson<AccountActionResponse>("/api/public/account/forgot-password", { email, languageCode });
}

export type ResetPasswordOutcome = "Success" | "InvalidOrExpired";

export async function resetPassword(
  userId: string,
  token: string,
  newPassword: string,
  confirmNewPassword: string,
): Promise<ApiResult<{ outcome: ResetPasswordOutcome }>> {
  return postJson<{ outcome: ResetPasswordOutcome }>("/api/public/account/reset-password", {
    userId,
    token,
    newPassword,
    confirmNewPassword,
  });
}
